// V177 — International Time Converter engine (standalone; does not touch lib/timezones.js).
// Uses the platform Intl API, which ships the IANA time-zone database, so DST,
// half-hour / 45-minute offsets and historical rule changes are handled natively.

import { COUNTRY_TIMEZONES, flagEmoji } from "./timezones";
import { COUNTRIES, COUNTRIES_BY_ISO2 } from "../data/countries";

export const IST_TZ = "Asia/Kolkata";
export const PUBLIC_SITE_ORIGIN = "https://anandtutorials.com";
export const PUBLIC_CONVERTER_PATH = "/time-converter";
export const PUBLIC_CONVERTER_URL = `${PUBLIC_SITE_ORIGIN}${PUBLIC_CONVERTER_PATH}`;
export const DIR_TO_IST = "to-ist";
export const DIR_FROM_IST = "from-ist";

// Curated, searchable city list: [label, country, IANA zone].
const CITIES = [
    ["Mexico City", "Mexico", "America/Mexico_City"], ["Cancún", "Mexico", "America/Cancun"], ["Tijuana", "Mexico", "America/Tijuana"],
    ["New York", "USA", "America/New_York"], ["Washington DC", "USA", "America/New_York"], ["Boston", "USA", "America/New_York"],
    ["Atlanta", "USA", "America/New_York"], ["Miami", "USA", "America/New_York"], ["New Jersey", "USA", "America/New_York"],
    ["Chicago", "USA", "America/Chicago"], ["Houston", "USA", "America/Chicago"], ["Dallas", "USA", "America/Chicago"],
    ["Denver", "USA", "America/Denver"], ["Phoenix", "USA", "America/Phoenix"], ["Los Angeles", "USA", "America/Los_Angeles"],
    ["San Francisco", "USA", "America/Los_Angeles"], ["Seattle", "USA", "America/Los_Angeles"], ["San Jose", "USA", "America/Los_Angeles"],
    ["Anchorage", "USA", "America/Anchorage"], ["Honolulu", "USA", "Pacific/Honolulu"],
    ["Toronto", "Canada", "America/Toronto"], ["Ottawa", "Canada", "America/Toronto"], ["Montreal", "Canada", "America/Toronto"],
    ["Vancouver", "Canada", "America/Vancouver"], ["Calgary", "Canada", "America/Edmonton"], ["Edmonton", "Canada", "America/Edmonton"],
    ["Winnipeg", "Canada", "America/Winnipeg"], ["Halifax", "Canada", "America/Halifax"], ["St. John's", "Canada", "America/St_Johns"],
    ["São Paulo", "Brazil", "America/Sao_Paulo"], ["Buenos Aires", "Argentina", "America/Argentina/Buenos_Aires"],
    ["Santiago", "Chile", "America/Santiago"], ["Bogotá", "Colombia", "America/Bogota"], ["Lima", "Peru", "America/Lima"],
    ["Caracas", "Venezuela", "America/Caracas"], ["Port of Spain", "Trinidad & Tobago", "America/Port_of_Spain"],
    ["London", "United Kingdom", "Europe/London"], ["Manchester", "United Kingdom", "Europe/London"], ["Birmingham", "United Kingdom", "Europe/London"],
    ["Dublin", "Ireland", "Europe/Dublin"], ["Lisbon", "Portugal", "Europe/Lisbon"], ["Paris", "France", "Europe/Paris"],
    ["Berlin", "Germany", "Europe/Berlin"], ["Frankfurt", "Germany", "Europe/Berlin"], ["Munich", "Germany", "Europe/Berlin"],
    ["Amsterdam", "Netherlands", "Europe/Amsterdam"], ["Brussels", "Belgium", "Europe/Brussels"], ["Zurich", "Switzerland", "Europe/Zurich"],
    ["Rome", "Italy", "Europe/Rome"], ["Milan", "Italy", "Europe/Rome"], ["Madrid", "Spain", "Europe/Madrid"],
    ["Vienna", "Austria", "Europe/Vienna"], ["Stockholm", "Sweden", "Europe/Stockholm"], ["Oslo", "Norway", "Europe/Oslo"],
    ["Copenhagen", "Denmark", "Europe/Copenhagen"], ["Helsinki", "Finland", "Europe/Helsinki"], ["Warsaw", "Poland", "Europe/Warsaw"],
    ["Athens", "Greece", "Europe/Athens"], ["Istanbul", "Turkey", "Europe/Istanbul"], ["Moscow", "Russia", "Europe/Moscow"],
    ["Kyiv", "Ukraine", "Europe/Kyiv"],
    ["Dubai", "UAE", "Asia/Dubai"], ["Abu Dhabi", "UAE", "Asia/Dubai"], ["Sharjah", "UAE", "Asia/Dubai"],
    ["Muscat", "Oman", "Asia/Muscat"], ["Doha", "Qatar", "Asia/Qatar"], ["Riyadh", "Saudi Arabia", "Asia/Riyadh"],
    ["Jeddah", "Saudi Arabia", "Asia/Riyadh"], ["Kuwait City", "Kuwait", "Asia/Kuwait"], ["Manama", "Bahrain", "Asia/Bahrain"],
    ["Tehran", "Iran", "Asia/Tehran"], ["Kabul", "Afghanistan", "Asia/Kabul"], ["Karachi", "Pakistan", "Asia/Karachi"],
    ["Kathmandu", "Nepal", "Asia/Kathmandu"], ["Dhaka", "Bangladesh", "Asia/Dhaka"], ["Colombo", "Sri Lanka", "Asia/Colombo"],
    ["Yangon", "Myanmar", "Asia/Yangon"], ["New Delhi", "India", IST_TZ], ["Mumbai", "India", IST_TZ], ["Jabalpur", "India", IST_TZ],
    ["Bhopal", "India", IST_TZ], ["Nagpur", "India", IST_TZ], ["Bengaluru", "India", IST_TZ],
    ["Singapore", "Singapore", "Asia/Singapore"], ["Kuala Lumpur", "Malaysia", "Asia/Kuala_Lumpur"], ["Bangkok", "Thailand", "Asia/Bangkok"],
    ["Jakarta", "Indonesia", "Asia/Jakarta"], ["Manila", "Philippines", "Asia/Manila"], ["Ho Chi Minh City", "Vietnam", "Asia/Ho_Chi_Minh"],
    ["Hong Kong", "Hong Kong", "Asia/Hong_Kong"], ["Shanghai", "China", "Asia/Shanghai"], ["Beijing", "China", "Asia/Shanghai"],
    ["Taipei", "Taiwan", "Asia/Taipei"], ["Seoul", "South Korea", "Asia/Seoul"], ["Tokyo", "Japan", "Asia/Tokyo"],
    ["Sydney", "Australia", "Australia/Sydney"], ["Melbourne", "Australia", "Australia/Melbourne"], ["Brisbane", "Australia", "Australia/Brisbane"],
    ["Perth", "Australia", "Australia/Perth"], ["Adelaide", "Australia", "Australia/Adelaide"], ["Darwin", "Australia", "Australia/Darwin"],
    ["Eucla", "Australia", "Australia/Eucla"], ["Auckland", "New Zealand", "Pacific/Auckland"], ["Chatham Islands", "New Zealand", "Pacific/Chatham"],
    ["Fiji", "Fiji", "Pacific/Fiji"],
    ["Johannesburg", "South Africa", "Africa/Johannesburg"], ["Cape Town", "South Africa", "Africa/Johannesburg"], ["Nairobi", "Kenya", "Africa/Nairobi"],
    ["Lagos", "Nigeria", "Africa/Lagos"], ["Cairo", "Egypt", "Africa/Cairo"], ["Casablanca", "Morocco", "Africa/Casablanca"],
    ["Dar es Salaam", "Tanzania", "Africa/Dar_es_Salaam"], ["Kampala", "Uganda", "Africa/Kampala"], ["Accra", "Ghana", "Africa/Accra"],
    ["Port Louis", "Mauritius", "Indian/Mauritius"],
];

const _dtfCache = new Map();
function _dtf(tz) {
    if (!_dtfCache.has(tz)) {
        _dtfCache.set(tz, new Intl.DateTimeFormat("en-US", {
            timeZone: tz, hourCycle: "h23",
            year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
        }));
    }
    return _dtfCache.get(tz);
}

export function isValidTimeZone(tz) {
    if (typeof tz !== "string" || tz.length > 64 || !/^[A-Za-z][A-Za-z0-9_+\-/]*$/.test(tz)) return false;
    try { _dtf(tz); return true; } catch { return false; }
}

// Wall-clock parts of a UTC instant (ms) in `tz`.
function wallParts(ms, tz) {
    const p = {};
    _dtf(tz).formatToParts(new Date(ms)).forEach(({ type, value }) => { p[type] = +value; });
    return { y: p.year, mo: p.month, d: p.day, h: p.hour % 24, mi: p.minute };
}

const wallAsUtc = (w) => Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi);

/** UTC offset in minutes of `tz` at instant `ms`. */
export function offsetMinutes(ms, tz) {
    const aligned = Math.floor(ms / 60000) * 60000;
    return Math.round((wallAsUtc(wallParts(aligned, tz)) - aligned) / 60000);
}

export function formatOffset(mins) {
    const sign = mins < 0 ? "−" : "+";
    const a = Math.abs(mins);
    return `UTC${sign}${String(Math.floor(a / 60)).padStart(2, "0")}:${String(a % 60).padStart(2, "0")}`;
}

export function isValidDateStr(s) {
    if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
    const [y, m, d] = s.split("-").map(Number);
    if (y < 1900 || y > 2100) return false;
    const dt = new Date(Date.UTC(y, m - 1, d));
    return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

export function isValidTimeStr(s) {
    return typeof s === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(s);
}

/**
 * Resolve a local wall-clock time in `tz` to UTC instants.
 * status: "ok" (1 instant) | "ambiguous" (2 — clocks fell back) | "nonexistent" (0 — clocks sprang forward) | "invalid".
 */
export function resolveWallTime(dateStr, timeStr, tz) {
    if (!isValidDateStr(dateStr) || !isValidTimeStr(timeStr) || !isValidTimeZone(tz)) return { status: "invalid", instants: [] };
    const [y, mo, d] = dateStr.split("-").map(Number);
    const [h, mi] = timeStr.split(":").map(Number);
    const target = Date.UTC(y, mo - 1, d, h, mi);
    const offsets = new Set([-2, -1, 0, 1, 2].map((k) => offsetMinutes(target + k * 12 * 3600000, tz)));
    const instants = [...offsets]
        .map((o) => target - o * 60000)
        .filter((ms) => wallAsUtc(wallParts(ms, tz)) === target)
        .filter((ms, i, arr) => arr.indexOf(ms) === i)
        .sort((a, b) => a - b);
    if (instants.length === 0) return { status: "nonexistent", instants };
    return { status: instants.length > 1 ? "ambiguous" : "ok", instants };
}

/** First valid wall time after a DST gap (e.g. 02:30 → 03:30 on spring-forward day). */
export function nextValidTime(dateStr, timeStr, tz) {
    const [h, mi] = timeStr.split(":").map(Number);
    for (let add = 15; add <= 180; add += 15) {
        const total = h * 60 + mi + add;
        if (total >= 1440) break;
        const t = `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
        if (resolveWallTime(dateStr, t, tz).status !== "nonexistent") return t;
    }
    return null;
}

// Abbreviations Intl doesn't expose (e.g. China CST, Singapore SGT) — taken from the demo form's data.
let _fallbackAbbr = null;
const FALLBACK_ABBR = () => {
    if (!_fallbackAbbr) {
        _fallbackAbbr = {};
        COUNTRY_TIMEZONES.forEach((c) => { if (!_fallbackAbbr[c.tz]) _fallbackAbbr[c.tz] = c.abbrFallback; });
    }
    return _fallbackAbbr;
};
// Extra search-only abbreviations Intl doesn't surface.
const EXTRA_ABBR = { "Asia/Jerusalem": "IST IDT", "Europe/Dublin": "IST GMT", "Asia/Karachi": "PKT", "Asia/Dhaka": "BST" };

function tzAbbr(ms, tz) {
    for (const loc of ["en-US", "en-GB", "en-IN", "en-AU"]) {
        try {
            const v = new Intl.DateTimeFormat(loc, { timeZone: tz, timeZoneName: "short" })
                .formatToParts(new Date(ms)).find((p) => p.type === "timeZoneName")?.value || "";
            if (/^[A-Z]{2,5}$/.test(v)) return v;
        } catch { /* ignore */ }
    }
    return "";
}

/** Describe instant `ms` as seen in `tz`. */
export function describeInstant(ms, tz, hour12 = true) {
    const w = wallParts(ms, tz);
    const date = new Intl.DateTimeFormat("en-GB", { timeZone: tz, weekday: "short", day: "numeric", month: "long", year: "numeric" }).format(new Date(ms));
    const time = hour12
        ? `${((w.h + 11) % 12) + 1}:${String(w.mi).padStart(2, "0")} ${w.h >= 12 ? "PM" : "AM"}`
        : `${String(w.h).padStart(2, "0")}:${String(w.mi).padStart(2, "0")}`;
    const off = offsetMinutes(ms, tz);
    return {
        tz, date, time, offsetMinutes: off, offset: formatOffset(off), abbr: tz === IST_TZ ? "IST" : (tzAbbr(ms, tz) || FALLBACK_ABBR()[tz] || ""),
        ymd: `${w.y}-${String(w.mo).padStart(2, "0")}-${String(w.d).padStart(2, "0")}`,
    };
}

export function dayShiftLabel(fromYmd, toYmd) {
    const diff = Math.round((Date.parse(`${toYmd}T00:00:00Z`) - Date.parse(`${fromYmd}T00:00:00Z`)) / 86400000);
    if (diff === 0) return { diff, label: "Same day" };
    if (diff === 1) return { diff, label: "Next day" };
    if (diff === -1) return { diff, label: "Previous day" };
    return { diff, label: `${diff > 0 ? "+" : ""}${diff} days` };
}

/**
 * Main entry. `dir` = to-ist (tz is the source) | from-ist (tz is the destination).
 * `pick` selects which instant to use when the source time is ambiguous (0 = earlier, 1 = later).
 */
export function convert({ dir, tz, date, time, pick = null, hour12 = true }) {
    const sourceTz = dir === DIR_FROM_IST ? IST_TZ : tz;
    const destTz = dir === DIR_FROM_IST ? tz : IST_TZ;
    const r = resolveWallTime(date, time, sourceTz);
    if (r.status === "invalid") return { status: "invalid" };
    if (r.status === "nonexistent") return { status: "nonexistent", sourceTz, suggestion: nextValidTime(date, time, sourceTz) };
    if (r.status === "ambiguous" && (pick === null || !r.instants[pick])) {
        return { status: "ambiguous", sourceTz, options: r.instants.map((ms) => describeInstant(ms, sourceTz, hour12)) };
    }
    const ms = r.instants[r.status === "ambiguous" ? pick : 0];
    const source = describeInstant(ms, sourceTz, hour12);
    const dest = describeInstant(ms, destTz, hour12);
    return { status: "ok", instant: ms, source, dest, dayShift: dayShiftLabel(source.ymd, dest.ymd) };
}

/* ----------------------------- Zone options ----------------------------- */
// Order: (1) the public demo form's COUNTRY_TIMEZONES rows (same labels), (2) every other
// country / sub-zone from data/countries.js, (3) leftover curated cities, (4) remaining IANA zones.

const prettyZone = (tz) => tz.split("/").slice(1).join(" / ").replace(/_/g, " ") || tz;
const _flag = (code) => (code && code.length === 2 ? flagEmoji(code) : "🌐");

// Alphabetic abbreviations Intl exposes in winter + summer (e.g. CST/CDT, GMT/BST).
function seasonalAbbrs(tz) {
    const year = new Date().getUTCFullYear();
    return [Date.UTC(year, 0, 15), Date.UTC(year, 6, 15)].map((ms) => tzAbbr(ms, tz)).filter(Boolean);
}

let _options = null;
export function getZoneOptions() {
    if (_options) return _options;
    const opts = [];
    const covered = new Set();
    const cityWords = {};
    CITIES.forEach(([city, country, tz]) => { cityWords[tz] = `${cityWords[tz] || ""} ${city} ${country}`; });
    const fallbackAbbr = FALLBACK_ABBR();

    const add = (kind, code, name, tz, abbrFallback, extra = "") => {
        if (!isValidTimeZone(tz)) return;
        const abbrs = [...new Set([abbrFallback || fallbackAbbr[tz], ...seasonalAbbrs(tz)].filter(Boolean))];
        const abbr = abbrs[0] || "";
        const countryName = COUNTRIES_BY_ISO2[code]?.name || "";
        const cityCountry = !code ? (name.split(", ").pop() || "") : "";
        opts.push({
            value: `${kind}:${code || "ZZ"}:${opts.length}|${tz}`, tz, code: code || "", flag: _flag(code), name, abbr, place: name,
            dial: dialDigits(code ? COUNTRIES_BY_ISO2[code]?.dial : DIAL_BY_NAME()[cityCountry.toLowerCase()]),
            dialLabel: dialLabel(code ? COUNTRIES_BY_ISO2[code]?.dial : DIAL_BY_NAME()[cityCountry.toLowerCase()]),
            label: `${_flag(code)} ${code ? `${code} — ` : ""}${name}${abbr ? ` (${abbr})` : ""}`,
            keywords: `${code} ${name} ${countryName} ${abbrs.join(" ")} ${tz} ${tz.replace(/[/_]/g, " ")} ${cityWords[tz] || ""} ${EXTRA_ABBR[tz] || ""} ${extra}`,
        });
        covered.add(tz);
    };

    COUNTRY_TIMEZONES.forEach((c) => add("form", c.code, c.name, c.tz, c.abbrFallback));
    COUNTRIES.forEach((c) => {
        const multi = c.timezones.length > 1;
        c.timezones.forEach((tz) => {
            if (covered.has(tz)) return;
            add("country", c.iso2, multi ? `${c.name} (${prettyZone(tz)})` : c.name, tz);
        });
    });
    CITIES.forEach(([city, country, tz]) => { if (!covered.has(tz)) add("city", "", `${city}, ${country}`, tz); });
    let zones = [];
    try { zones = Intl.supportedValuesOf("timeZone"); } catch { zones = []; }
    // Skip legacy aliases (e.g. Asia/Calcutta) of zones already listed.
    const canon = (tz) => { try { return new Intl.DateTimeFormat("en-US", { timeZone: tz }).resolvedOptions().timeZone; } catch { return tz; } };
    const coveredCanon = new Set([...covered].map(canon));
    zones.forEach((tz) => {
        if (covered.has(tz) || coveredCanon.has(canon(tz))) return;
        add("zone", "", `${prettyZone(tz)} (${tz})`, tz);
    });
    _options = opts;
    return _options;
}

// V186 — Key markets pinned at the top of the selector (fixed list, no tracking).
export const PINNED_ZONES = ["Asia/Dubai", "America/New_York", "Europe/London", "America/Toronto",
    "Australia/Sydney", "Asia/Qatar", "Asia/Riyadh", "Asia/Muscat", "Asia/Singapore"];

export function getPinnedZoneOptions() {
    const opts = getZoneOptions();
    return PINNED_ZONES.map((tz) => opts.find((o) => o.tz === tz)).filter(Boolean);
}

/** Friendly place name for messages, e.g. "Oman", "USA — New York / Eastern". */
export function zoneLabel(tz, optionValue) {
    const opts = getZoneOptions();
    const hit = (optionValue && opts.find((o) => o.value === optionValue)) || opts.find((o) => o.tz === tz);
    if (hit) return hit.place;
    return tz === IST_TZ ? "India" : prettyZone(tz);
}

/** Token search: every typed word must match; exact code / abbreviation matches rank first. */
// International dialling-code search (e.g. "+968", "968", "+1"): digits only, matched exactly.
const dialDigits = (d) => String(d || "").replace(/\D/g, "");
// Display form: "+971", "+1", NANP area codes as "+1-264".
const dialLabel = (d) => { const x = dialDigits(d); return !x ? "" : x.length === 4 && x[0] === "1" ? `+1-${x.slice(1)}` : `+${x}`; };
let _dialByName = null;
const DIAL_BY_NAME = () => {
    if (!_dialByName) {
        _dialByName = {};
        COUNTRIES.forEach((c) => { _dialByName[c.name.toLowerCase()] = c.dial; });
    }
    return _dialByName;
};

function searchByDialCode(query, opts) {
    const q = (query || "").trim().replace(/\s+/g, "");
    if (!/^\+?\d{1,4}$/.test(q)) return null;
    const d = dialDigits(q);
    const scored = [];
    opts.forEach((o, i) => {
        if (!o.dial) return;
        // +1 is shared: USA/Canada (+1) first, then Caribbean +1xxx countries.
        if (o.dial === d) scored.push([0, i, o]);
        else if (d === "1" && o.dial.length === 4 && o.dial.startsWith("1")) scored.push([1, i, o]);
    });
    return scored.length ? scored.sort((a, b) => a[0] - b[0] || a[1] - b[1]).map((x) => x[2]) : null;
}

export function searchZoneOptions(query, opts = getZoneOptions()) {
    const byDial = searchByDialCode(query, opts);
    if (byDial) return byDial;
    const tokens = (query || "").toLowerCase().replace(/[,]/g, " ").split(/\s+/).filter(Boolean);
    if (!tokens.length) return opts;
    const scored = [];
    opts.forEach((o, i) => {
        const hay = `${o.label} ${o.keywords}`.toLowerCase();
        const words = hay.split(/[^a-z0-9+\-]+/).filter(Boolean);
        // Tokens with "/" match the IANA id as a substring; others must start a word.
        if (!tokens.every((t) => (t.includes("/") ? hay.includes(t) : words.some((w) => w.startsWith(t))))) return;
        const code = o.code.toLowerCase(), abbr = o.abbr.toLowerCase(), name = o.name.toLowerCase();
        let score = 4;
        if (tokens.some((t) => t === code)) score = 0;
        else if (tokens.some((t) => t === abbr || o.keywords.toLowerCase().split(/\s+/).includes(t))) score = 1;
        else if (name.startsWith(tokens[0])) score = 2;
        else if (name.includes(tokens[0])) score = 3;
        scored.push([score, i, o]);
    });
    return scored.sort((a, b) => a[0] - b[0] || a[1] - b[1]).map((x) => x[2]);
}

export function browserTimeZone() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { return ""; }
}

export function todayInZone(tz, now = Date.now()) {
    const w = wallParts(now, isValidTimeZone(tz) ? tz : "UTC");
    return `${w.y}-${String(w.mo).padStart(2, "0")}-${String(w.d).padStart(2, "0")}`;
}

/* --------------------------- Share URL handling -------------------------- */

/** Builds a public link. Only non-personal values (direction, zone, date, time) are ever encoded. */
export function buildShareUrl({ dir, tz, date, time, hour12 }, origin = PUBLIC_SITE_ORIGIN) {
    const q = new URLSearchParams();
    if (dir === DIR_TO_IST || dir === DIR_FROM_IST) q.set("dir", dir);
    if (isValidTimeZone(tz)) q.set("tz", tz);
    if (isValidDateStr(date)) q.set("date", date);
    if (isValidTimeStr(time)) q.set("time", time);
    if (hour12 === false) q.set("fmt", "24");
    const qs = q.toString();
    return `${origin}${PUBLIC_CONVERTER_PATH}${qs ? `?${qs}` : ""}`;
}

// V187 — Links people share go through the server preview page (branded WhatsApp card),
// which forwards straight to /time-converter with the same params.
export const SHARE_PREVIEW_PATH = "/api/share/time-converter";
export function buildPreviewShareUrl(params, origin = PUBLIC_SITE_ORIGIN) {
    const direct = buildShareUrl(params, "");
    return `${origin}${SHARE_PREVIEW_PATH}${direct.slice(PUBLIC_CONVERTER_PATH.length)}`;
}
export const PUBLIC_SHARE_URL = buildPreviewShareUrl({});

/** Parses untrusted URL params; returns only validated fields + whether anything was rejected. */
export function parseShareParams(search) {
    const q = new URLSearchParams(search || "");
    const out = {};
    let rejected = false;
    const dir = q.get("dir"), tz = q.get("tz"), date = q.get("date"), time = q.get("time"), fmt = q.get("fmt");
    if (fmt) { if (fmt === "24" || fmt === "12") out.fmt = fmt; else rejected = true; }
    if (dir) { if (dir === DIR_TO_IST || dir === DIR_FROM_IST) out.dir = dir; else rejected = true; }
    if (tz) { if (isValidTimeZone(tz)) out.tz = tz; else rejected = true; }
    if (date) { if (isValidDateStr(date)) out.date = date; else rejected = true; }
    if (time) { if (isValidTimeStr(time)) out.time = time; else rejected = true; }
    return { values: out, rejected };
}

/* ------------------------------ Messages -------------------------------- */

const line = (d, place) => `${d.date}, ${d.time} — ${place} (${d.abbr ? `${d.abbr}, ` : ""}${d.offset})`;

export function resultText(res, placeLabel) {
    const srcPlace = res.source.tz === IST_TZ ? "India" : placeLabel;
    const dstPlace = res.dest.tz === IST_TZ ? "India" : placeLabel;
    return `${line(res.source, srcPlace)}\n= ${line(res.dest, dstPlace)} [${res.dayShift.label}]`;
}

export function conversionWhatsAppMessage(res, placeLabel, link) {
    if (res.dest.tz === IST_TZ) {
        return `Hello Sir/Ma'am,\n\nThank you for sharing your preferred demo timing.\n\nYour preferred time:\n${line(res.source, placeLabel)}.\n\nEquivalent time in India:\n${line(res.dest, "India")}.\n\nYou can review or compare the timing here:\n${link}\n\nPlease confirm whether this timing works for you. Our team will review availability and confirm the demo class.`;
    }
    return `Hello Sir/Ma'am,\n\nHere is the proposed demo timing converted to your local time.\n\nTime in India:\n${line(res.source, "India")}.\n\nEquivalent time for you:\n${line(res.dest, placeLabel)}.\n\nYou can review or compare the timing here:\n${link}\n\nPlease confirm whether this timing works for you. Our team will review availability and confirm the demo class.`;
}

export const converterLinkMessage = (url = PUBLIC_SHARE_URL) => `Hello Sir/Ma'am,\n\nPlease use our International Time Converter to check your preferred demo-class timing in your local time zone and its equivalent time in India (IST).\n\nOpen the converter here: ${url}\n\nYou can use this link from your mobile phone, tablet, or computer.`;
export const CONVERTER_LINK_MESSAGE = converterLinkMessage();

/* -------------------------- Demo-form handoff --------------------------- */

const isIndiaZone = (tz) => tz === IST_TZ || tz === "Asia/Calcutta";

/** Earliest bookable demo date (mirrors backend V125 rule: on/after tomorrow in IST). */
export function demoMinDate(now = Date.now()) {
    const [y, m, d] = todayInZone(IST_TZ, now).split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}

/**
 * Demo-form handoff for the parent's LOCAL time.
 * Returns null (India / DST-ambiguous / no result), { tooSoon, minDate }, or { path }.
 */
export function buildDemoBookingPath(res, tz, now = Date.now()) {
    if (!res || res.status !== "ok" || !isValidTimeZone(tz) || isIndiaZone(tz)) return null;
    const local = describeInstant(res.instant, tz, false);
    if (resolveWallTime(local.ymd, local.time, tz).status !== "ok") return null;
    const minDate = demoMinDate(now);
    if (local.ymd < minDate) return { tooSoon: true, minDate };
    const q = new URLSearchParams({ mode: "foreign", demo_date: local.ymd, demo_time: local.time, demo_tz: tz });
    return { path: `/register?${q.toString()}` };
}

/** Validated demo prefill from /register?mode=foreign&demo_date&demo_time&demo_tz (untrusted input). */
export function parseDemoPrefill(search) {
    const q = new URLSearchParams(search || "");
    if (q.get("mode") !== "foreign") return null;
    const date = q.get("demo_date"), time = q.get("demo_time"), tz = q.get("demo_tz");
    if (!isValidDateStr(date) || !isValidTimeStr(time)) return null;
    return { date, time, tz: isValidTimeZone(tz) && !isIndiaZone(tz) ? tz : "" };
}
